<template>
  <Loader :value="promises.fetchOne" :message="$metaInfo.title" class="webhook-mutate">
    <div class="container view">
      <HeaderComponent :title="$metaInfo.title" :breadcrumb="breadcrumbs" />
      <Card tag="form" @submit.prevent="submit" class="webhook-mutate__form">
        <template #default>
          <div class="webhook-mutate__inputs">
            <InputComponent
              v-model="data.name"
              :label="labels.name"
              required
              :is-valid="validator.fields.name.valid"
              :validation-message="validator.fields.name.error"
            />
            <InputComponent
              v-model="data.url"
              :label="labels.url"
              required
              :is-valid="validator.fields.url.valid"
              :validation-message="validator.fields.url.error"
            />
            <InputComponent
              v-model="data.secret"
              :label="labels.secret"
              required
              :is-valid="validator.fields.secret.valid"
              :validation-message="validator.fields.secret.error"
            />
            <InputComponent
              label="Conditions"
              :value="data.conditions"
              :is-valid="validator.fields.conditions.valid"
              :validation-message="validator.fields.conditions.error"
            >
              <PrismEditor v-model.lazy="data.conditions" language="json" line-numbers />
            </InputComponent>
            <InputComponent
              label="Headers"
              :value="data.headers"
              :is-valid="validator.fields.headers.valid"
              :validation-message="validator.fields.headers.error"
            >
              <PrismEditor v-model.lazy="data.headers" language="json" line-numbers />
            </InputComponent>
            <InputComponent :label="labels.events">
              <template #default>
                <div class="webhook-mutate__scroller">
                  <ChecklistComponent v-model="data.events" :items="webhooks" class="elder-input__element" />
                </div>
              </template>
            </InputComponent>
            <InputComponent :label="$t('status')">
              <div class="elder-input__element">
                <CheckboxComponent v-model="data.enabled" :label="labels.enabled" />
                <CheckboxComponent
                  v-if="$store.getters.isSuperadmin"
                  v-model="data.isSystem"
                  :label="labels.isSystem"
                  class="mt-1"
                />
              </div>
            </InputComponent>
          </div>
        </template>
        <template #footer>
          <Alert class="alert"
            ><CheckboxComponent
              v-model="tokenCompliance"
              :disabled="!isNew"
              required
              :is-valid="validator.fields.tokenCompliance.valid"
              :validation-message="validator.fields.tokenCompliance.error"
              :label="$t('integrationCompliance')"
          /></Alert>

          <ButtonComponent
            :label="isNew ? $t('create') : $t('save')"
            theme="primary"
            type="submit"
            :disabled="!validator.passes"
            :icon="['fal', isNew ? 'plus' : 'arrow-to-bottom']"
            :promise="promises.mutate"
          />

          <DropdownComponent v-if="!isNew" :close-on-click="false">
            <ButtonComponent :icon="{ icon: ['fal', 'ellipsis-v'], size: 'lg' }" />
            <template #dropdown>
              <ButtonComponent :label="$t('delete')" @click="removeDialog = true" :icon="['fal', 'times']" />
            </template>
          </DropdownComponent>
        </template>
      </Card>

      <Card v-if="!isNew" :title="$tc('log', 2)" class="webhook-mutate__logs">
        <Logs :value="item.logs" />
      </Card>

      <RemoveComponent
        :show.sync="removeDialog"
        :title="$metaInfo.title"
        :action="() => remove({ id })"
        @onSuccess="
          $router.push({
            name: 'settings',
            params: { step: 'webhooks' },
          })
        "
        :resource="$tc('webhook')"
      />
    </div>
  </Loader>
</template>

<script>
import Validator from '@/mixins/validator-v2'
import Model from '@/mixins/Model'
import { mapState, mapActions, mapMutations } from 'vuex'
import { mapi18n } from '@/i18n'

import HeaderComponent from '@/components/Header'
import PrismEditor from '@/components/PrismEditor'
import ChecklistComponent from '@/components/Select/Checklist'
import Card from '@/components/Card'
import RemoveComponent from '@/components/Remove'
import { DropdownComponent } from 'vue-elder-dropdown'
import { InputComponent } from 'vue-elder-input'
import { CheckboxComponent } from 'vue-elder-checkbox'
import { AlertComponent as Alert } from 'vue-elder-alert'
import Logs from '@/components/Webhook/Logs'

export default {
  mixins: [
    Validator({
      data: 'mergedData',
      labels: 'labels',
      rules: {
        name: 'required|string',
        url: 'required|webhookurl',
        events: 'array',
        headers: 'json',
        conditions: 'json',
        secret: 'required|string|min:15',
        tokenCompliance: 'accepted|required',
      },
    }),
    Model({
      source: 'item',
      model: {
        id: null,
        name: null,
        enabled: false,
        url: null,
        events: [],
        secret: null,
        headers: '{}',
        conditions: '{}',
        isSystem: false,
      },
      guard: true,
    }),
  ],
  props: {
    id: String,
  },
  data() {
    return {
      removeDialog: false,
      tokenComplianceState: null,
    }
  },
  watch: {
    id: {
      handler(id) {
        if (!id) return this.set(null)
        return this.fetch({ id })
      },
      immediate: true,
    },
  },
  computed: {
    ...mapState(['enums']),
    ...mapState('Webhook', ['item', 'promises']),
    ...mapi18n('labels', {
      name: 'name',
      enabled: 'enabled',
      url: 'url',
      events: ['event', 2],
      secret: 'webhookSecret',
      isSystem: 'webhookIsSystem',
    }),
    tokenCompliance: {
      get: function () {
        return this.isNew ? this.tokenComplianceState : true
      },
      set: function (newValue) {
        return (this.tokenComplianceState = newValue)
      },
    },
    mergedData() {
      return {
        ...this.data,
        tokenCompliance: this.tokenCompliance,
      }
    },
    isNew() {
      return !this.id
    },
    breadcrumbs() {
      return [
        { title: this.$tc('setting', 2), action: { name: 'settings' } },
        { title: this.$tc('webhook', 2), action: { name: 'settings', params: { step: 'webhooks' } } },
        { title: this.$metaInfo.title },
      ]
    },
    webhooks() {
      return this.enums.Webhooks.reduce((res, cur) => {
        cur.operations.forEach(operation => {
          res.push({
            value: `${cur.resource}:${operation}`,
            label: `${cur.resource}:${operation}`,
          })
        })
        return res
      }, [])
    },
  },
  methods: {
    ...mapActions('Webhook', ['fetch', 'mutate', 'remove']),
    ...mapMutations('Webhook', ['set']),
    submit() {
      this.mutate({
        id: this.id,
        ...this.getData({ diff: true }),
      }).then(data => {
        if (!this.isNew) return
        if (this.$route.query.returnTo) return this.$router.push(this.$route.query.returnTo)
        this.$router.replace({
          name: 'webhook-edit',
          params: { id: data.id },
        })
      })
    },
  },
  metaInfo() {
    return {
      title: this.isNew ? this.$t('createWebhook') : this.$path('item.name'),
    }
  },
  components: {
    InputComponent,
    HeaderComponent,
    DropdownComponent,
    ChecklistComponent,
    CheckboxComponent,
    RemoveComponent,
    PrismEditor,
    Card,
    Logs,
    Alert,
  },
}
</script>

<style lang="scss">
.webhook-mutate {
  background-color: $background-grey;
  $limit: 950px;

  .alert {
    width: 100%;
    .elder-checkbox__box {
      border-color: css-darken('border-color', 15%);
    }
  }

  &__form {
    margin: 0 auto;
    max-width: $limit;

    [type='submit'] {
      margin-right: auto;
    }
  }

  &__inputs {
    display: grid;
    align-items: center;
    grid-template-columns: 1fr;
    grid-gap: $form-gap-row / 2 $form-gap-column;
    align-items: flex-start;
  }

  &__logs {
    margin: 2rem auto 0 auto;
    max-width: $limit;
  }

  &__scroller {
    max-height: 350px;
    overflow-y: scroll;

    @include scrollShadow();
  }
}
</style>
